import { environment } from 'src/environments/environment';

/** Normalize an in-app route fragment to `/segment/...` (no query/hash). */
export function normalizeAppRoutePath(routePath: string): string {
  const raw = (routePath || '').trim();
  if (!raw) {
    return '/';
  }
  const withoutQuery = raw.split('?')[0].split('#')[0];
  if (withoutQuery.startsWith('/')) {
    return withoutQuery;
  }
  return `/${withoutQuery}`;
}

/**
 * Current Angular route path from the browser.
 * With hash routing, the route lives in `location.hash` (e.g. `#/i-am-Trustee`).
 */
export function getCurrentAppRoutePath(fallback = '/'): string {
  if (typeof window === 'undefined') {
    return normalizeAppRoutePath(fallback);
  }

  if (environment.useHashRouting) {
    const hash = window.location.hash || '';
    if (hash.startsWith('#/')) {
      const path = hash.slice(1).split('?')[0];
      return path || normalizeAppRoutePath(fallback);
    }
    if (hash.startsWith('#')) {
      const path = hash.slice(1).split('?')[0];
      return normalizeAppRoutePath(path || fallback);
    }
  }

  const pathname = (window.location.pathname || '').split('?')[0];
  return pathname || normalizeAppRoutePath(fallback);
}

/** True when `path` matches the current in-app route (hash-aware). */
export function isCurrentAppRoute(path: string): boolean {
  const current = getCurrentAppRoutePath().toLowerCase();
  const target = normalizeAppRoutePath(path).toLowerCase();
  return current === target || current.startsWith(`${target}/`);
}

/**
 * Full browser URL for external redirects (Stripe, Scytale, desktop client return, etc.).
 * Preserves `/#/` when hash routing is enabled.
 */
export function buildExternalAppUrl(routePath: string, origin?: string): string {
  const baseOrigin = (origin || resolveAppOrigin()).replace(/\/$/, '');
  const path = normalizeAppRoutePath(routePath);
  if (environment.useHashRouting) {
    return `${baseOrigin}/#${path}`;
  }
  return `${baseOrigin}${path}`;
}

/** Route-only redirect target for APIs/backend (no origin, no hash prefix). */
export function buildAppRedirectRoute(routePath: string): string {
  return normalizeAppRoutePath(routePath).replace(/^\//, '');
}

function resolveAppOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return (environment.clientBaseUrl || '').replace(/\/$/, '');
}
